import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import {
    TimeRangeSelector as TimeRangeSelectorFromModule,
    ErrorAlert,
    PadContainer,
    Fields,
    HorizontalFlex,
    timeAndPrecisionTypes,
} from 'smartsuite-ui';
import moment from 'moment';
import { useLabelContext } from '../shared/hooks/labelContext';
import { dateTimeFormat } from '../shared/constants/constants';
import {
    getIsFixedRangeInOrder,
    getIsLastNextRangeValid,
    isRangeValueWithinMin,
    isRangeValueWithinMax,
    getRangeDomain,
    isFixedRangeWithinMin,
    isFixedRangeWithinMax,
} from '../shared/filtering/filtersModalUtils';

import { validateTimeAndPrecision } from '../shared/filtering/timeAndPrecisionValidation';

export default function TimeRangeSelector(props) {
    const { translationLabels, dashboardLabels, interpolateString } =
        useLabelContext();

    const handleTimeRangeChange = (updatedTimeRange) => {
        let timeRange = {
            ...updatedTimeRange,
        };

        if (timeRange.startDateTime) {
            if (timeRange.timeRangeType === timeAndPrecisionTypes.fixedDay) {
                timeRange.startDateTime = moment(
                    timeRange.startDateTime
                ).startOf('day');

                timeRange.endDateTime = moment(timeRange.startDateTime)
                    .startOf('day')
                    .add(1, 'days');
            }

            timeRange.startDateTime = moment(timeRange.startDateTime).format(
                dateTimeFormat
            );
        }

        if (timeRange.endDateTime) {
            timeRange.endDateTime = moment(timeRange.endDateTime).format(
                dateTimeFormat
            );
        }

        if (_.has(timeRange, 'selectedDateRangeOption')) {
            timeRange = {
                ...timeRange,
                ...timeRange.selectedDateRangeOption,
            };
        }
        props.saveTimeAndPrecision(timeRange);
    };

    const renderErrorMessage = () => {
        const isFixedRangeInOrder = getIsFixedRangeInOrder(
            props.timeAndPrecision
        );

        if (!isFixedRangeInOrder) {
            return <ErrorAlert>{dashboardLabels.invalidFixedRange}</ErrorAlert>;
        }

        const fixedRangeDomain =
            props.timeAndPrecisionConfiguration.timeAndPrecisionDomains
                ?.fixed ?? {};

        const fixedRangeIsWithinMax = isFixedRangeWithinMax(
            props.timeAndPrecision,
            props.timeAndPrecisionConfiguration.timeAndPrecisionDomains
        );

        const fixedRangeIsWithinMin = isFixedRangeWithinMin(
            props.timeAndPrecision,
            props.timeAndPrecisionConfiguration.timeAndPrecisionDomains
        );
        // review Logic
        if (!fixedRangeIsWithinMax) {
            return (
                <ErrorAlert>{`${dashboardLabels.timeRangeExceedsMax} ${
                    fixedRangeDomain.max
                } ${
                    dashboardLabels[fixedRangeDomain.precisionType]
                }`}</ErrorAlert>
            );
        }

        if (!fixedRangeIsWithinMin) {
            return (
                <ErrorAlert>{`${dashboardLabels.timeRangeBelowMin} ${fixedRangeDomain.min} `}</ErrorAlert>
            );
        }

        const isLastNextRangeValid = getIsLastNextRangeValid(
            props.timeAndPrecision,
            props.timeAndPrecisionConfiguration.timeAndPrecisionDomains
        );

        const rangeDomain = getRangeDomain(
            props.timeAndPrecision,
            props.timeAndPrecisionConfiguration.timeAndPrecisionDomains
        );
        const isLastNextRangeWithinMin = isRangeValueWithinMin(
            props.timeAndPrecision,
            props.timeAndPrecisionConfiguration.timeAndPrecisionDomains
        );
        const isLastNextRangeWithinMax = isRangeValueWithinMax(
            props.timeAndPrecision,
            props.timeAndPrecisionConfiguration.timeAndPrecisionDomains
        );

        if (!isLastNextRangeWithinMin) {
            return (
                <ErrorAlert>{`${dashboardLabels.timeRangeBelowMin} ${
                    rangeDomain.min
                } ${
                    dashboardLabels[props.timeAndPrecision.rangeType]
                }`}</ErrorAlert>
            );
        }

        if (!isLastNextRangeWithinMax) {
            return (
                <ErrorAlert>{`${dashboardLabels.timeRangeExceedsMax} ${
                    rangeDomain.max
                } ${
                    dashboardLabels[props.timeAndPrecision.rangeType]
                }`}</ErrorAlert>
            );
        }

        if (!isLastNextRangeValid) {
            return null;
        }

        const { isTimeAndPrecisionValid, errors } = validateTimeAndPrecision(
            props.timeAndPrecision,
            interpolateString,
            dashboardLabels,
            true
        );

        if (isTimeAndPrecisionValid) {
            return null;
        }

        return (
            <ErrorAlert>
                <ul>
                    {errors.map((error) => (
                        <li key={error}>{error}</li>
                    ))}
                </ul>
            </ErrorAlert>
        );
    };

    if (!props.timeAndPrecisionConfiguration.showTimeAndPrecision) {
        return null;
    }
    let timeRange = { ...props.timeAndPrecision };
    if (timeRange.startDateTime) {
        timeRange = {
            ...timeRange,
            startDateTime: moment(timeRange.startDateTime).toDate(),
        };
    }
    if (timeRange.endDateTime) {
        timeRange = {
            ...timeRange,
            endDateTime: moment(timeRange.endDateTime).toDate(),
        };
    }
    return (
        <>
            <PadContainer
                cssClassModifiers={['pad-medium', 'no-pad-left-right']}
            >
                {renderErrorMessage()}
                <HorizontalFlex
                    cssClassModifiers={['flex-wrap', 'align-flex-end']}
                >
                    <Fields
                        cssClassModifiers={[
                            'no-background',
                            'no-pad-top-bottom',
                        ]}
                    >
                        <TimeRangeSelectorFromModule
                            labels={{
                                ...translationLabels,
                                ...dashboardLabels,
                            }}
                            predefinedDateRangeOptions={
                                props.timeAndPrecisionConfiguration
                                    .predefinedDateRanges
                            }
                            predicateLabel={dashboardLabels.timeRange}
                            saveTimeAndPrecision={handleTimeRangeChange}
                            timeAndPrecision={timeRange}
                            timeAndPrecisionOptions={
                                props.timeAndPrecisionConfiguration
                                    .timeRangeTypeOptions
                            }
                        />
                    </Fields>
                </HorizontalFlex>
            </PadContainer>
            <hr className="divider divider--criteria" />
        </>
    );
}
TimeRangeSelector.propTypes = {
    timeAndPrecisionConfiguration: PropTypes.instanceOf(Object).isRequired,
    saveTimeAndPrecision: PropTypes.func,
    timeAndPrecision: PropTypes.instanceOf(Object).isRequired,
};
